import { InjectionToken } from '@angular/core';
import { PunchoutProtocolHandler } from './handlers/punchout-protocol.handler';

/**
 * The single handler that the runtime resolves to per session (cXML vs OCI vs
 * a customer-specific variant). Wired through `RuntimeProtocolDelegate`, which
 * picks the matching entry from `PUNCHOUT_PROTOCOL_HANDLERS` based on the
 * logged-in user's `PunchoutCustomerConfig`.
 */
export const PUNCHOUT_HANDLER = new InjectionToken<PunchoutProtocolHandler>('PUNCHOUT_HANDLER');

/**
 * Multi-provider registry of all mappers the app knows about. The admin UI
 * reads this list to populate the per-config identifier dropdown, and the
 * runtime delegate looks up the matching mapper by `(protocol, identifier)`.
 *
 * Every entry must declare a unique `identifier` — duplicates across the
 * registry are a logic bug. The two defaults (`oci-default`, `cxml-default`)
 * are wired in `PunchoutModule`.
 *
 * ## Adding a customer-specific mapper
 *
 * 1. Create a class in the customer project that extends one of the standard
 *    handlers (or `PunchoutProtocolHandler` directly) and overrides the bits
 *    that differ — typically `buildPayload` / `mapRow`. Set a unique
 *    `identifier` and human-readable `label`:
 *
 *    ```ts
 *    @Injectable({ providedIn: 'root' })
 *    export class BestdriveJaegerCxmlHandler extends CxmlPunchoutHandler {
 *      override readonly identifier = 'bestdrive-jaeger';
 *      override readonly label = 'Bestdrive (Jaeger)';
 *      // override buildPayload / itemIn / ... as needed
 *    }
 *    ```
 *
 * 2. Register it as a multi-provider in the customer's `AppModule` (or any
 *    feature module pulled in by it):
 *
 *    ```ts
 *    providers: [
 *      {
 *        provide: PUNCHOUT_PROTOCOL_HANDLERS,
 *        useExisting: BestdriveJaegerCxmlHandler,
 *        multi: true
 *      }
 *    ]
 *    ```
 *
 * The new identifier shows up in the admin dropdown for the matching format
 * automatically, and the runtime starts dispatching to it as soon as a config
 * picks it.
 */
export const PUNCHOUT_PROTOCOL_HANDLERS = new InjectionToken<PunchoutProtocolHandler[]>(
  'PUNCHOUT_PROTOCOL_HANDLERS'
);
